// ============================================
// pages/gunny-bags/GunnyPosList.tsx — Gunny Purchase Orders list (real API)
// Upstream: MARKFED → vendor PO flow before delivery/utilization.
// ============================================
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Loader2, Plus, Edit2, CheckCircle, XCircle, Send, PackageCheck, Share2, Truck } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { seasonsAPI, gunnySuppliersAPI, gunnyPosAPI } from '../../api/services';
import { UserRole, formatAmount, num } from '../../types/markfed';
import type { Season } from '../../types/markfed';
import GunnyAllotModal from '../../components/gunny/GunnyAllotModal';
import GunnyDeliveriesModal from '../../components/gunny/GunnyDeliveriesModal';
import { Pagination } from '../../components/common/Pagination/Pagination';
import { usePagination } from '../../components/common/Pagination/usePagination';

const STATUS_CONFIG: Record<string, { label: string; cls: string }> = {
  draft:            { label: 'Draft',            cls: 'bg-yellow-50 text-yellow-700 border-yellow-300' },
  submitted:        { label: 'Submitted',        cls: 'bg-blue-50 text-blue-700 border-blue-300' },
  dm_verified:      { label: 'DM Verified',      cls: 'bg-orange-50 text-orange-700 border-orange-300' },
  manager_approved: { label: 'Approved',         cls: 'bg-green-50 text-green-700 border-green-300' },
  fulfilled:        { label: 'Fulfilled',        cls: 'bg-purple-50 text-purple-700 border-purple-300' },
  closed:           { label: 'Closed',           cls: 'bg-gray-100 text-gray-600 border-gray-300' },
  rejected:         { label: 'Rejected',         cls: 'bg-red-50 text-red-700 border-red-300' },
};

export const GunnyPosList: React.FC = () => {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const canSubmit = hasRole(UserRole.AO_CAO, UserRole.SUPER_ADMIN);
  const canVerify = hasRole(UserRole.DM, UserRole.AO_CAO, UserRole.SUPER_ADMIN);
  const canApprove = hasRole(UserRole.MD, UserRole.SUPER_ADMIN);
  const canAllot = hasRole(UserRole.DM, UserRole.AO_CAO, UserRole.MANAGER_PROCUREMENT, UserRole.SUPER_ADMIN);

  const [seasons, setSeasons] = useState<Season[]>([]);
  const [selectedSeasonId, setSelectedSeasonId] = useState<number>(0);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [pos, setPos] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [supplierFilter, setSupplierFilter] = useState<number>(0);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [allotPo, setAllotPo] = useState<any | null>(null);
  const [deliveriesPo, setDeliveriesPo] = useState<any | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const [s, sup] = await Promise.all([seasonsAPI.list(), gunnySuppliersAPI.list()]);
        const list: Season[] = Array.isArray(s.data) ? s.data : [];
        setSeasons(list);
        setSuppliers(sup.data);
        const active = list.find((x) => x.is_active) || list[0];
        if (active) setSelectedSeasonId(active.id);
      } catch (err: any) {
        setMessage({ type: 'error', text: err?.response?.data?.message || 'Failed to load' });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!selectedSeasonId) return;
    (async () => {
      setLoading(true);
      try {
        const params: any = {};
        if (supplierFilter) params.supplier_id = supplierFilter;
        const res = await gunnyPosAPI.list(selectedSeasonId, params);
        setPos(Array.isArray(res.data) ? res.data : []);
      } catch (err: any) {
        setMessage({ type: 'error', text: err?.response?.data?.message || 'Failed to load POs' });
      } finally {
        setLoading(false);
      }
    })();
  }, [selectedSeasonId, supplierFilter]);

  const filtered = useMemo(() => {
    if (statusFilter === 'all') return pos;
    return pos.filter((p) => (p.status || 'draft') === statusFilter);
  }, [pos, statusFilter]);

  const { page, pageSize, totalPages, totalItems, pagedItems, setPage, setPageSize } =
    usePagination(filtered, 25);

  const advance = async (id: number, fn: () => Promise<any>, next: string, msg: string) => {
    setActionLoading(id);
    setMessage(null);
    try {
      await fn();
      setPos((prev) => prev.map((p) => (p.id === id ? { ...p, status: next } : p)));
      setMessage({ type: 'success', text: msg });
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.response?.data?.message || `Failed: ${msg}` });
    } finally {
      setActionLoading(null);
    }
  };

  const doSubmit = (id: number) => window.confirm('Submit PO for DM approval?')
    && advance(id, () => gunnyPosAPI.submit(selectedSeasonId, id), 'submitted', 'Submitted');
  const doVerify = (id: number) => window.confirm('Approve this PO? DM approval is final.')
    && advance(id, () => gunnyPosAPI.verify(selectedSeasonId, id), 'manager_approved', 'Approved');
  const doFulfill = (id: number) => window.confirm('Mark PO as fulfilled (vendor delivered all bags)?')
    && advance(id, () => gunnyPosAPI.fulfill(selectedSeasonId, id), 'fulfilled', 'Fulfilled');
  const doReject = async (id: number) => {
    const reason = window.prompt('Rejection reason:');
    if (!reason?.trim()) return;
    await advance(id, () => gunnyPosAPI.reject(selectedSeasonId, id, reason.trim()), 'rejected', 'Rejected');
  };

  const formatDate = (s?: string) => {
    if (!s) return '--';
    try { return new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return s; }
  };

  if (loading && pos.length === 0) {
    return <div className="max-w-7xl mx-auto flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-amber-600 animate-spin" /><span className="ml-2 text-gray-500">Loading...</span></div>;
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <FileText className="w-7 h-7 text-amber-600" />
            Gunny Purchase Orders
          </h1>
          <p className="text-sm text-gray-500 mt-1">Upstream PO workflow — order bags from vendors before delivery</p>
        </div>
        <button onClick={() => navigate('/gunny-bags/procurement/create')}
          className="flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-lg text-sm">
          <Plus className="w-4 h-4" />New PO
        </button>
      </div>

      <div className="mb-4 bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Season</label>
            <select value={selectedSeasonId} onChange={(e) => setSelectedSeasonId(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
              {seasons.map((s) => (<option key={s.id} value={s.id}>{s.season_name}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Supplier</label>
            <select value={supplierFilter} onChange={(e) => setSupplierFilter(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
              <option value={0}>All</option>
              {suppliers.map((s) => (<option key={s.id} value={s.id}>{s.supplier_name}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
              <option value="all">All</option>
              <option value="draft">Draft</option>
              <option value="submitted">Submitted</option>
              <option value="manager_approved">Approved</option>
              <option value="fulfilled">Fulfilled</option>
              <option value="closed">Closed</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>
      </div>

      {message && (
        <div className={`mb-4 flex items-center gap-2 p-3 rounded-lg text-sm ${
          message.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          {message.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
          {message.text}
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-xs">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left font-semibold text-gray-600 uppercase">#</th>
                <th className="px-3 py-3 text-left font-semibold text-gray-600 uppercase">PO No</th>
                <th className="px-3 py-3 text-left font-semibold text-gray-600 uppercase">PO Date</th>
                <th className="px-3 py-3 text-left font-semibold text-gray-600 uppercase">Supplier</th>
                <th className="px-3 py-3 text-left font-semibold text-gray-600 uppercase">Commodity</th>
                <th className="px-3 py-3 text-left font-semibold text-gray-600 uppercase">Delivery</th>
                <th className="px-3 py-3 text-right font-semibold text-gray-600 uppercase">Bags</th>
                <th className="px-3 py-3 text-right font-semibold text-gray-600 uppercase">Total</th>
                <th className="px-3 py-3 text-right font-semibold text-gray-600 uppercase">Advance</th>
                <th className="px-3 py-3 text-center font-semibold text-gray-600 uppercase">Status</th>
                <th className="px-3 py-3 text-center font-semibold text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.length === 0 ? (
                <tr><td colSpan={11} className="px-4 py-12 text-center text-gray-400">No POs. Click "New PO".</td></tr>
              ) : pagedItems.map((p, i) => {
                const st = p.status || 'draft';
                const cfg = STATUS_CONFIG[st] || { label: st, cls: 'bg-gray-100 text-gray-500 border-gray-300' };
                const isLoading = actionLoading === p.id;
                // Resolve commodity display: prefer parent-level (single commodity), else
                // aggregate from items, else show "—" when no commodity is recorded anywhere.
                // "Mixed" only fires when items genuinely span multiple distinct commodities.
                let commodityDisplay: { kind: 'single' | 'mixed' | 'none'; names: string[] } = { kind: 'none', names: [] };
                if (p.commodity?.name) {
                  commodityDisplay = { kind: 'single', names: [p.commodity.name] };
                } else {
                  const itemNames = Array.from(new Set((p.items || []).map((it: any) => it.commodity?.name).filter(Boolean))) as string[];
                  if (itemNames.length === 1) commodityDisplay = { kind: 'single', names: itemNames };
                  else if (itemNames.length > 1) commodityDisplay = { kind: 'mixed', names: itemNames };
                }
                const itemCount = (p.items || []).length;
                return (
                  <tr key={p.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 text-gray-500">{(page - 1) * pageSize + i + 1}</td>
                    <td className="px-3 py-2 font-medium text-gray-900">{p.po_number}</td>
                    <td className="px-3 py-2 text-gray-600">{formatDate(p.po_date)}</td>
                    <td className="px-3 py-2 text-gray-700">{p.supplier?.supplier_name || '--'}</td>
                    <td className="px-3 py-2 text-xs">
                      {commodityDisplay.kind === 'single' && (
                        <span className="inline-block px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded">{commodityDisplay.names[0]}</span>
                      )}
                      {commodityDisplay.kind === 'mixed' && (
                        <span title={commodityDisplay.names.join(' · ')}
                          className="inline-block px-2 py-0.5 bg-amber-50 text-amber-700 rounded">
                          mixed ({commodityDisplay.names.length})
                        </span>
                      )}
                      {commodityDisplay.kind === 'none' && <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-3 py-2 text-gray-600">
                      {p.deliveryDistrict?.name || '--'}
                      {p.delivery_date && <div className="text-[10px] text-gray-400">{formatDate(p.delivery_date)}</div>}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {num(p.total_bags).toLocaleString('en-IN')}
                      {itemCount > 0 && <div className="text-[10px] text-gray-400">{itemCount} item{itemCount === 1 ? '' : 's'}</div>}
                    </td>
                    <td className="px-3 py-2 text-right font-medium">{formatAmount(num(p.total_amount))}</td>
                    <td className="px-3 py-2 text-right">{formatAmount(num(p.advance_amount))}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold border ${cfg.cls}`}>{cfg.label}</span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <div className="flex items-center justify-center gap-1 flex-wrap">
                        {(st === 'draft' || st === 'rejected') && (
                          <button onClick={() => navigate(`/gunny-bags/procurement/edit/${p.id}`)}
                            className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded">
                            <Edit2 className="w-3 h-3" />Edit
                          </button>
                        )}
                        {st === 'draft' && canSubmit && (
                          <button onClick={() => doSubmit(p.id)} disabled={isLoading}
                            className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded disabled:opacity-50">
                            {isLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}Submit
                          </button>
                        )}
                        {/* DM approval is final — single Approve action takes a 'submitted' PO straight to manager_approved.
                            Legacy 'dm_verified' rows can also be cleared with the same button. */}
                        {(st === 'submitted' || st === 'dm_verified') && (canVerify || canApprove) && (
                          <button onClick={() => doVerify(p.id)} disabled={isLoading}
                            className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold text-white bg-green-600 hover:bg-green-700 rounded disabled:opacity-50">
                            <CheckCircle className="w-3 h-3" />Approve
                          </button>
                        )}
                        {st === 'manager_approved' && canApprove && (
                          <button onClick={() => doFulfill(p.id)} disabled={isLoading}
                            className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold text-white bg-purple-600 hover:bg-purple-700 rounded disabled:opacity-50">
                            <PackageCheck className="w-3 h-3" />Fulfill
                          </button>
                        )}
                        {['manager_approved', 'fulfilled'].includes(st) && canAllot && (
                          <>
                            <button onClick={() => setDeliveriesPo(p)} disabled={isLoading}
                              className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded disabled:opacity-50">
                              <Truck className="w-3 h-3" />Deliveries
                            </button>
                            <button onClick={() => setAllotPo(p)} disabled={isLoading}
                              className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold text-white bg-amber-600 hover:bg-amber-700 rounded disabled:opacity-50">
                              <Share2 className="w-3 h-3" />Allot
                            </button>
                          </>
                        )}
                        {['submitted', 'dm_verified'].includes(st) && (canVerify || canApprove) && (
                          <button onClick={() => doReject(p.id)} disabled={isLoading}
                            className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold text-white bg-red-600 hover:bg-red-700 rounded disabled:opacity-50">
                            <XCircle className="w-3 h-3" />Reject
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {filtered.length > 0 && (
          <div className="px-4 py-3 border-t bg-gray-50">
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
              totalItems={totalItems}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              itemLabel="POs"
            />
          </div>
        )}
      </div>

      <GunnyAllotModal
        po={allotPo}
        onClose={() => setAllotPo(null)}
        onAllotted={() => setMessage({ type: 'success', text: 'Allotted to society — new utilization row created.' })}
      />
      <GunnyDeliveriesModal
        po={deliveriesPo}
        onClose={() => setDeliveriesPo(null)}
        onChanged={() => setMessage({ type: 'success', text: 'Delivery recorded against PO.' })}
      />
    </div>
  );
};

export default GunnyPosList;
